import { useEffect } from 'react';
import { SEO_CONFIG, type SEOConfig } from '../utils/seo';

/**
 * Hook to dynamically update SEO meta tags
 * Usage: useSEO({ title: 'Page Title', description: 'Page description' })
 */
export function useSEO(config: SEOConfig) {
  useEffect(() => {
    const {
      title,
      description,
      canonical,
      ogImage = SEO_CONFIG.defaultImage,
      schema,
      noindex = false,
      keywords = [],
    } = config;

    // Update title
    if (title) {
      document.title = title;
    }

    // Helper function to set/update meta tags
    const setMeta = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(`meta[${selector}="${attribute}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(selector, attribute);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update description
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }

    // Update keywords
    if (keywords.length > 0) {
      setMeta('name', 'keywords', keywords.join(', '));
    }

    // Update canonical URL
    if (canonical) {
      const fullUrl = `${SEO_CONFIG.siteUrl}${canonical}`;
      setMeta('property', 'og:url', fullUrl);
      setMeta('name', 'twitter:url', fullUrl);

      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', fullUrl);
    }

    // Update Open Graph image
    if (ogImage) {
      const imageUrl = ogImage.url.startsWith('http')
        ? ogImage.url
        : `${SEO_CONFIG.siteUrl}${ogImage.url}`;
      
      setMeta('property', 'og:image', imageUrl);
      setMeta('name', 'twitter:image', imageUrl);
      
      if (ogImage.alt) {
        setMeta('property', 'og:image:alt', ogImage.alt);
        setMeta('name', 'twitter:image:alt', ogImage.alt);
      }
    }

    // Update robots for noindex pages
    if (noindex) {
      setMeta('name', 'robots', 'noindex, nofollow');
    } else {
      setMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Add JSON-LD schema if provided
    if (schema) {
      // Remove existing schemas first
      const existingScripts = document.querySelectorAll('script[data-schema="page"]');
      existingScripts.forEach(s => s.remove());

      const schemas = Array.isArray(schema) ? schema : [schema];
      
      schemas.forEach((s, index) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-schema', 'page');
        script.setAttribute('data-schema-index', index.toString());
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      const schemaScripts = document.querySelectorAll('script[data-schema="page"]');
      schemaScripts.forEach(s => s.remove());
    };
  }, [config]);
}
